"use client";

import { Alignment, Fit, Layout, useRive } from "@rive-app/react-webgl2";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";

import {
  RiveSafetyBoundary,
  useRiveCleanup,
  useRiveLoadFailure,
  useRiveWebGLContextGuard,
} from "@/components/rive/rive-safety-boundary";

import {
  computePanelHeight,
  HORIZONTAL_HEIGHT_ANCHORS,
  STACKED_HEIGHT_ANCHORS,
} from "./footer-banner-panel-height";
import type { HeightAnchors } from "./footer-banner-panel-height";
import type { FooterBannerVariant } from "./types";

const STATE_MACHINE = "State Machine 1";
const HOVER_ANIMATION = "Hover";
const IDLE_ANIMATION = "Nohover";

const RIVE_SRC: Record<FooterBannerVariant, string> = {
  stacked: "/footer-banner/footer-banner-w-stacked.riv",
  // TODO(WEB-423): swap in final designer export when footer-banner-w-horizontal.riv is ready
  horizontal: "/footer-banner/footer-banner-w-horizontal.riv",
};

const HEIGHT_ANCHORS: Record<FooterBannerVariant, HeightAnchors> = {
  stacked: STACKED_HEIGHT_ANCHORS,
  horizontal: HORIZONTAL_HEIGHT_ANCHORS,
};

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return prefersReducedMotion;
}

function useMobileLayout() {
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 623px)");
    const update = () => setMobile(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return mobile;
}

function useBannerHeight(anchors: HeightAnchors) {
  const containerRef = useRef<HTMLDivElement>(null);
  const initial = anchors[0] ?? STACKED_HEIGHT_ANCHORS[0];
  const [height, setHeight] = useState(initial[1]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateHeight = () => {
      setHeight(computePanelHeight(element.clientWidth, anchors));
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, [anchors]);

  return { containerRef, height };
}

interface FooterBannerRiveProps {
  variant: FooterBannerVariant;
}

export function FooterBannerRive({ variant }: FooterBannerRiveProps) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const mobile = useMobileLayout();
  const anchors = HEIGHT_ANCHORS[variant];
  const { containerRef, height } = useBannerHeight(anchors);

  const fallback = (
    <FooterBannerFallback containerRef={containerRef} height={height} />
  );

  return (
    <RiveSafetyBoundary fallback={fallback}>
      <FooterBannerRiveCanvas
        variant={variant}
        prefersReducedMotion={prefersReducedMotion}
        mobile={mobile}
        containerRef={containerRef}
        height={height}
      />
    </RiveSafetyBoundary>
  );
}

interface FooterBannerRiveCanvasProps extends FooterBannerRiveProps {
  prefersReducedMotion: boolean;
  mobile: boolean;
  containerRef: RefObject<HTMLDivElement | null>;
  height: number;
}

function FooterBannerRiveCanvas({
  variant,
  prefersReducedMotion,
  mobile,
  containerRef,
  height,
}: FooterBannerRiveCanvasProps) {
  const { loadFailed, handleLoadError, handleContextLost } =
    useRiveLoadFailure();
  const alignment = mobile ? Alignment.BottomCenter : Alignment.Center;
  const layoutScaleFactor = mobile ? 1.12 : 1;

  const layout = useMemo(
    () =>
      new Layout({
        fit: Fit.Cover,
        alignment,
        layoutScaleFactor,
      }),
    [alignment, layoutScaleFactor]
  );

  const { RiveComponent, rive, canvas } = useRive(
    {
      src: RIVE_SRC[variant],
      stateMachines: STATE_MACHINE,
      autoplay: !prefersReducedMotion,
      layout,
      onLoadError: handleLoadError,
    },
    { shouldResizeCanvasToContainer: true }
  );

  useRiveWebGLContextGuard(canvas, rive, handleContextLost);
  useRiveCleanup(rive);

  const setHovered = useCallback(
    (hovered: boolean) => {
      if (!rive) return;
      rive.play(hovered ? HOVER_ANIMATION : IDLE_ANIMATION);
    },
    [rive]
  );

  useEffect(() => {
    if (!rive) return;
    rive.layout = new Layout({
      fit: Fit.Cover,
      alignment,
      layoutScaleFactor,
    });
    rive.play(IDLE_ANIMATION);
    if (!rive.isPlaying) {
      rive.play(STATE_MACHINE);
    }
  }, [rive, alignment, layoutScaleFactor]);

  if (loadFailed) {
    return <FooterBannerFallback containerRef={containerRef} height={height} />;
  }

  return (
    <div
      ref={containerRef}
      aria-hidden
      className="rounded-ds-xl relative w-full overflow-hidden"
      data-testid="footer-banner-rive"
      style={{ height }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setHovered(true)}
      onBlurCapture={() => setHovered(false)}
    >
      <div className="bg-ds-gradient-primary absolute inset-0">
        <RiveComponent
          className={`absolute inset-0 size-full [&_canvas]:absolute [&_canvas]:inset-0 [&_canvas]:size-full ${
            variant === "stacked" ? "scale-[1.12]" : ""
          }`}
        />
      </div>
    </div>
  );
}

function FooterBannerFallback({
  containerRef,
  height,
}: Pick<FooterBannerRiveCanvasProps, "containerRef" | "height">) {
  return (
    <div
      ref={containerRef}
      aria-hidden
      className="rounded-ds-xl relative w-full overflow-hidden"
      data-testid="footer-banner-rive"
      style={{ height }}
    >
      <div className="bg-ds-gradient-primary absolute inset-0">
        <div
          data-testid="footer-banner-rive-fallback"
          className="absolute inset-0"
        />
      </div>
    </div>
  );
}
